import { AppTopNav } from 'app/modules/shared/AppTopNav';
import { BasePageProps, Sponsor } from 'app/types';
import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SponsorService } from 'app/services/SponsorService';
import { AppModal } from 'app/modules/shared/modals/AppModal';

const ManageSponsors: React.FC<BasePageProps> = (props: BasePageProps) => {
    const [sponsors, setSponsors] = useState<Sponsor[]>([]);
    const [form, setForm] = useState<Partial<Sponsor>>({
        name: '',
        description: '',
        subdomain: '',
        sponsoredByText: '',
    });
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [subdomainStatus, setSubdomainStatus] = useState<string | undefined>(undefined);

    const sponsorService = new SponsorService();

    useEffect(() => {
        const fetchSponsors = async () => {
            setLoading(true);
            try {
                const { sponsors } = await sponsorService.getAllSponsors();
                const domainStatus = await sponsorService.getSubdomainStatus();
                setSubdomainStatus(domainStatus);
                setSponsors(sponsors);
            } catch (error) {
                console.error('Error fetching sponsors:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSponsors();
    }, []);

    // check and update subdomain status every 5 seconds
    useEffect(() => {   
        
        const interval = setInterval(async () => {
            try {
                if(!editingId) return; // Only check if editing a sponsor
                const domainStatus = await sponsorService.getSubdomainStatus();
                setSubdomainStatus(domainStatus);
            } catch (error) {
                console.error('Error checking subdomain status:', error);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [sponsorService, editingId]);

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required').max(100, 'Name must be at most 100 characters'),
        subdomain: Yup.string()
            .required('Subdomain is required')
            .max(50, 'Subdomain must be at most 50 characters')
            .matches(/^[a-z0-9-]+$/, 'Subdomain must be lowercase and can only contain letters, numbers, and hyphens')
            .lowercase('Subdomain must be lowercase'),
        description: Yup.string().max(500, 'Description must be at most 500 characters'),
        sponsoredByText: Yup.string()
            .required('Tagline is required')
            .max(500, 'Tagline must be at most 500 characters'),
    });

    const handleSubmitFormik = async (values: Partial<Sponsor>) => {
        setLoading(true);
        try {
            if (editingId) {
                const updatedSponsor = await sponsorService.updateSponsor(editingId, values);
                setSponsors((prev) =>
                    prev.map((sponsor) => (sponsor.id === editingId ? updatedSponsor : sponsor))
                );
                setEditingId(null);
            } else {
                const newSponsor = await sponsorService.createSponsor(values);
                setSponsors((prev) => [...prev, newSponsor]);
            }
        } catch (error) {
            console.error('Error saving sponsor:', error);
        } finally {
            setLoading(false);
            closeModal();
        }
    };

    const handleEdit = (id: string) => {
        const sponsor = sponsors.find((s) => s.id === id);
        if (sponsor) {
            setForm(sponsor);
            setEditingId(id);
            openModal();
        }
    };

    const handleDelete = async () => {
        if (deleteId) {
            setLoading(true);
            try {
                await sponsorService.deleteSponsor(deleteId);
                setSponsors((prev) => prev.filter((sponsor) => sponsor.id !== deleteId));
            } catch (error) {
                console.error('Error deleting sponsor:', error);
            } finally {
                setLoading(false);
                setDeleteId(null);
                setShowDeleteModal(false);
            }
        }
    };

    const openModal = () => setShowModal(true);
    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm({ name: '', description: '', subdomain: '', sponsoredByText: '' });
    };

    const openDeleteModal = (id: string) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setDeleteId(null);
        setShowDeleteModal(false);
    };

    return (
        <div className="chatPage">
            <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs} />
            <div className="container mt-5">
                <div className="card shadow-sm">
                    <div className="card-body">
                        <Button variant="primary" onClick={openModal} className="mb-3" disabled={loading}>
                            Add Sponsor
                        </Button>
                        {loading ? (
                            <div className="text-center">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        ) : (
                            <ul className="list-group">
                                {sponsors.map((sponsor) => (
                                    <li key={sponsor.id} className="list-group-item d-flex justify-content-between align-items-start">
                                        <div className="ms-2 me-auto">
                                            <div className="fw-bold">{sponsor.name}</div>
                                            <p className="mb-1">{sponsor.description}</p>
                                            <small className="text-muted">{sponsor.subdomain}</small>
                                            <br />
                                            <small className="text-muted">{sponsor.sponsoredByText}</small>
                                        </div>
                                        <div>
                                            <button
                                                className="btn btn-sm btn-outline-primary me-2"
                                                onClick={() => handleEdit(sponsor.id)}
                                                disabled={loading}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => openDeleteModal(sponsor.id)}
                                                disabled={loading}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </li>
                                ))}
                                {(sponsors == null || sponsors.length === 0) && !loading && (
                                    <Alert variant="warning">No sponsors added yet</Alert>
                                )}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal for Create/Edit */}
            <Modal show={showModal} onHide={closeModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingId ? 'Edit Sponsor' : 'Add Sponsor'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant={subdomainStatus === 'AVAILABLE' ? 'success' : 'danger'} className="mb-3">Domain status : {subdomainStatus}</Alert>
                    <Formik
                        initialValues={form}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmitFormik}
                        enableReinitialize
                    >
                        {({ isSubmitting, setFieldValue }) => (
                            <Form className="row g-3">
                                <div className="col-12">
                                    <label htmlFor="name" className="form-label">Name</label>
                                    <Field
                                        type="text"
                                        id="name"
                                        name="name"
                                        className="form-control"
                                        placeholder="Name"
                                    />
                                    <ErrorMessage name="name" component="div" className="text-danger" />
                                </div>
                                <div className="col-12">
                                    <label htmlFor="subdomain" className="form-label">Subdomain</label>
                                    <div className="input-group">
                                        <Field
                                            type="text"
                                            id="subdomain"
                                            name="subdomain"
                                            className="form-control"
                                            placeholder="Subdomain"
                                            readOnly={!!form.subdomain || subdomainStatus !== 'AVAILABLE'}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const value = e.target.value.toLowerCase();
                                                setFieldValue('subdomain', value);
                                            }}
                                        />
                                        <span className="input-group-text">.videojobfairs.com</span>
                                        {!!form.subdomain && editingId && (
                                            <Button
                                                variant="outline-danger"
                                                type="button"
                                                disabled={loading}
                                                onClick={async () => {
                                                    await sponsorService.deleteSubdomainAssociation(editingId);
                                                    setFieldValue('subdomain', '');
                                                    setForm({ ...form, subdomain: '' });
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </div>
                                    <ErrorMessage name="subdomain" component="div" className="text-danger" />
                                </div>
                                <div className="col-12">
                                    <label htmlFor="description" className="form-label">Description</label>
                                    <Field
                                        as="textarea"
                                        id="description"
                                        name="description"
                                        className="form-control"
                                        placeholder="Description"
                                    />
                                    <ErrorMessage name="description" component="div" className="text-danger" />
                                </div>
                                <div className="col-12">
                                    <label htmlFor="sponsoredByText" className="form-label">Sponsored By Text</label>
                                    <Field
                                        type="text"
                                        id="sponsoredByText"
                                        name="sponsoredByText"
                                        className="form-control"
                                        placeholder="Sponsored By Text"
                                    />
                                    <ErrorMessage name="sponsoredByText" component="div" className="text-danger" />
                                </div>
                                <div className="col-12 text-end">
                                    <Button type="submit" variant="primary" disabled={isSubmitting || loading || subdomainStatus !== 'AVAILABLE'}>
                                        {editingId ? 'Update' : 'Create'}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>

            {/* Confirmation Modal for Delete */}
            <AppModal
                show={showDeleteModal}
                title="Confirm Delete"
                variant="YesNo"
                onHide={closeDeleteModal}
                onReject={closeDeleteModal}
                onSuccess={handleDelete}
                successText="Yes, Delete"
                rejectText="Cancel"
                successBtnVariant="danger"
            >
                Are you sure you want to delete this sponsor?
            </AppModal>
        </div>
    );
};

export default ManageSponsors;